import React, { useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Button,
  Spinner,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import ResumeSearchBar from "../components/ResumeSearchBar";
import ResumeFilters from "../components/ResumeFilters";
import ResumeTable from "../components/ResumeTable";
import ResumeUploadModal from "../components/ResumeUploadModal";
import BulkUploadModal from "../components/BulkUploadModal";
import useResumeSearch from "../hooks/useResumeSearch";
import useResumeUpload from "../hooks/useResumeUpload";
import { parseFiltersFromQuery, toQueryString } from "../utils/queryBuilder";
import resumeRepositoryService from "../services/resumeRepositoryService";

const ResumeRepositoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [filters, setFilters] = useState(() =>
    parseFiltersFromQuery(location.search),
  );

  const uploadModal = useDisclosure();
  const bulkUploadModal = useDisclosure();

  const { data, isLoading, isError, refetch } = useResumeSearch(filters);
  const { uploadMutation, bulkUploadMutation } = useResumeUpload();

  const rows = useMemo(() => data?.data || [], [data]);

  const syncQuery = (nextFilters) => {
    const query = toQueryString(nextFilters);
    navigate(`/recruiting/resume-db${query ? `?${query}` : ""}`, {
      replace: true,
    });
  };

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value, page: key === "page" ? value : 1 };
    setFilters(next);
    syncQuery(next);
  };

  const resetFilters = () => {
    const next = parseFiltersFromQuery("");
    setFilters(next);
    syncQuery(next);
  };

  const handleUpload = async (formData) => {
    try {
      await uploadMutation.mutateAsync(formData);
      toast({ title: "Resume uploaded", status: "success", duration: 2500 });
      refetch();
    } catch (error) {
      toast({
        title: "Upload failed",
        description:
          error?.response?.data?.message || "Unable to upload resume",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleBulkUpload = async (formData) => {
    try {
      await bulkUploadMutation.mutateAsync(formData);
      toast({ title: "Bulk upload queued", status: "success", duration: 2500 });
      refetch();
    } catch (error) {
      toast({
        title: "Bulk upload failed",
        description: error?.response?.data?.message || "Unable to upload files",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleView = (resume) => {
    navigate(`/recruiting/resume-db/resume/${resume._id}`);
  };

  const handleDownload = async (resume) => {
    try {
      const response = await resumeRepositoryService.downloadResume(resume._id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.setAttribute("download", resume.originalFileName || "resume");
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast({ title: "Download failed", status: "error", duration: 2500 });
    }
  };

  return (
    <Box p={6}>
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between" align="center">
          <Heading size="md">Resume Repository</Heading>
          <HStack>
            <Button colorScheme="blue" onClick={uploadModal.onOpen}>
              Upload
            </Button>
            <Button variant="outline" onClick={bulkUploadModal.onOpen}>
              Bulk Upload
            </Button>
          </HStack>
        </HStack>

        <ResumeSearchBar
          value={filters.q}
          onChange={(e) => updateFilter("q", e.target.value)}
          placeholder="Search by name, email, company or keyword"
        />

        <ResumeFilters
          filters={filters}
          onFilterChange={updateFilter}
          onReset={resetFilters}
        />

        {isLoading ? <Spinner /> : null}
        <ResumeTable
          rows={isError ? [] : rows}
          onView={handleView}
          onDownload={handleDownload}
        />
      </VStack>

      <ResumeUploadModal
        isOpen={uploadModal.isOpen}
        onClose={uploadModal.onClose}
        onUpload={handleUpload}
        isLoading={uploadMutation.isPending}
      />

      <BulkUploadModal
        isOpen={bulkUploadModal.isOpen}
        onClose={bulkUploadModal.onClose}
        onUpload={handleBulkUpload}
        isLoading={bulkUploadMutation.isPending}
      />
    </Box>
  );
};

export default ResumeRepositoryPage;
